import { sendConversionEvent } from '~/helpers/algolia-insights'

/* eslint-disable no-underscore-dangle */
/**
 * Store: Cart
 * -----------------------------------------------------------------------------
 * Cart state and events.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */

/**
 * How long a removed line item stays restorable for, in milliseconds.
 * @type {Number}
 */
const undoRemoveTimeout = 3000

/**
 * Timeouts which discard restorable line items, keyed by line item key.
 * - Kept outside of state as timeout IDs aren't renderable data.
 * @type {Object}
 */
const undoRemoveTimers = {}

/**
 * Clears the pending discard timeout of a restorable line item.
 * @param {String} key - Line item key.
 */
function clearUndoRemoveTimer(key) {
  if (!undoRemoveTimers[key]) {
    return
  }

  clearTimeout(undoRemoveTimers[key])
  delete undoRemoveTimers[key]
}

/**
 * Returns a line item and its add-ons as a flat array.
 * @param {Object} lineItem - Grouped line item.
 * @returns {Array}
 */
function getLineItemGroup(lineItem) {
  const addons = lineItem?.addons || []

  return [lineItem, ...addons]
}

export default {
  namespaced: true,

  state: () => ({
    discountCodes: [],
    history: [],
    removedItems: [],
    response: {
      attributes: {},
      cart_level_discount_applications: [],
      currency: null,
      item_count: 0,
      items_subtotal_price: 0,
      items: [],
      note: null,
      original_total_price: 0,
      requires_shipping: false,
      token: null,
      total_discount: 0,
      total_price: 0,
      total_weight: 0,
    },
    status: {
      items: [],
      label: 'loading',
    },
  }),

  getters: {

    /**
     * Get grouped cart items with recently removed items re-inserted at the
     * position they were removed from so they can be restored.
     * @returns {Array}
     */
    getCartItemsWithRemovals(state, getters) {
      const items = [...getters.getGroupedCart.items]

      const removedItems = [...state.removedItems].sort((a, b) => {
        return a.index - b.index
      })

      removedItems.forEach((removedItem) => {
        const index = Math.min(removedItem.index, items.length)

        items.splice(index, 0, {
          ...removedItem.lineItem,
          removed: true,
          restoring: removedItem.restoring,
          undoTimeout: undoRemoveTimeout,
        })
      })

      return items
    },

    /**
     * Get grouped main products with add-ons array.
     * @returns {Array}
     */
    getGroupedCart(state) {
      const cartItems = [...state.response.items]
      const addonProducts = []
      const addons = {}
      const mainProducts = []

      /**
       * Go through each cart item and group by main product or add-on based on
       * 'addon' line item property.
       * - Also builds object of add-on arrays based on 'groupId' to be combined
       *   with main products.
       */
      cartItems.forEach((cartItem) => {
        const item = { ...cartItem }
        const groupId = item.properties?._groupId

        if (item.properties?._addon && groupId) {
          addonProducts.push(item)

          if (addons[groupId]) {
            addons[groupId].push(item)
            return
          }

          addons[groupId] = [item]
          return
        }

        mainProducts.push(item)
      })

      /**
       * Combine main products with add-ons based on 'groupId'.
       */
      const items = mainProducts.map((mainProduct) => {
        return {
          ...mainProduct,
          addons: addons[mainProduct.properties?._groupId],
        }
      })

      return {
        items,
        item_count: items.length,
      }
    },
  },

  mutations: {

    /**
     * Sets the discount codes currently applied to the cart.
     * @param {Array} discountCodes - Applied discount codes.
     */
    SET_DISCOUNT_CODES(state, discountCodes) {
      state.discountCodes = discountCodes
    },

    /**
     * Adds new event to history.
     * - Latest event is added to the start of the history array.
     * @param {Object} event - Cart event to add.
     */
    SET_HISTORY(state, event) {
      state.history.unshift(event)
    },

    /**
     * Removes a line item from the restorable removed items.
     * @param {String} key - Key of the line item to discard.
     */
    DELETE_REMOVED_ITEM(state, key) {
      state.removedItems = state.removedItems.filter((item) => item.key !== key)
    },

    /**
     * Discards all restorable removed items.
     */
    DELETE_REMOVED_ITEMS(state) {
      state.removedItems = []
    },

    /**
     * Adds or replaces a restorable removed line item.
     * @param {Object} removedItem - Removed line item snapshot.
     * @param {Number} removedItem.index - Position the item was removed from.
     * @param {String} removedItem.key - Key of the removed line item.
     * @param {Object} removedItem.lineItem - Grouped line item which was
     * removed.
     * @param {Boolean} removedItem.restoring - Whether the item is currently
     * being restored.
     */
    SET_REMOVED_ITEM(state, removedItem) {
      state.removedItems = [
        ...state.removedItems.filter((item) => item.key !== removedItem.key),
        removedItem,
      ]
    },

    /**
     * Sets the response state.
     * @param {Boolean} response - Cart response data.
     */
    SET_RESPONSE(state, response) {
      state.response = response
    },

    /**
     * Sets current cart status.
     * @param {Object} payload - Mutation payload.
     * @param {Array} payload.items - Array of item keys being updated.
     * @param {String} payload.label - Status description, can be `loading`,
     * `loaded`, `adding`, `removing`, `clearing`, `updating`, `updated`, or
     * `error`.
     */
    SET_STATUS(state, { items, label }) {
      state.status = {
        items: items ? items : [],
        label,
      }
    },
  },

  actions: {

    /**
     * Actions cart requests.
     * @param {Object} payload - Action payload.
     * @param {String} payload.action - `add`, `clear`, `update`, or `change`.
     * @param {String} payload.body - Stringified body of request.
     * @param {Number} [payload.itemsLeft] - Items left in queue, only set
     * response when no more items are left in queue.
     * @returns {Promise}
     */
    actionCartUpdate({ commit, dispatch }, { action, body, itemsLeft = 0 } = {}) {
      return new Promise(async(resolve, reject) => {
        try {
          let response = await fetch(cnvs.routes.cart[action], {
            body,
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          })

          const responseOk = response.ok
          response = await response.json()

          /**
           * Get cart response even if there are errors.
           * - If you try to add more quantity than available inventory it will
           *   throw an error, but still add the maximum it can so cart needs to
           *   update regardless.
           */
          if (!itemsLeft) {
            await dispatch('setResponse')
          }

          if (!responseOk) {
            commit('SET_STATUS', { label: 'error' })
            reject(response)
            return
          }

          resolve(response)

        } catch (error) {
          commit('SET_STATUS', { label: 'error' })
          reject(error?.description || error)
        }
      })
    },

    /**
     * Adds item(s) to the cart.
     * @param {Array} items - Items to add to cart.
     * @param {Number} items[].id - Item variant ID.
     * @param {Object} [items[].properties] - Object of key: value pairs of
     * properties to add.
     * @param {Number} items[].quantity - Quantity to add.
     * @returns {Promise}
     */
    addItem({ commit, dispatch }, items) {
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', {
            items: items.map((item) => item.id),
            label: 'adding',
          })

          const response = await dispatch('actionCartUpdate', {
            action: 'add',
            body: JSON.stringify({ items }),
          })

          // Send Algolia conversion events for all items with query context
          const algoliaIndex = window.cnvs?.algolia?.indices?.products
          items.forEach((item) => {
            const eventIndex = item.__indexName || algoliaIndex
            if (item.__queryID && eventIndex) {
              sendConversionEvent({
                indexName: eventIndex,
                objectID: item.id,
                queryID: item.__queryID,
              })
            }
          })

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Applies a discount code to the cart.
     * - Shopify replaces all discount codes on each update so previously
     *   applied codes have to be sent again.
     * - Resolves with `true` when the code successfully applied, Shopify
     *   silently ignores codes which aren't valid for the cart or customer.
     * @param {String} code - Discount code to apply.
     * @returns {Promise}
     */
    applyDiscountCode({ commit, dispatch, state }, code) {
      return new Promise(async(resolve, reject) => {
        const discountCode = String(code || '').trim()

        if (!discountCode) {
          reject(new Error('No discount code provided'))
          return
        }

        try {
          commit('SET_STATUS', { label: 'updating' })

          const codes = state.discountCodes.map((discount) => discount.code)

          const alreadyApplied = codes.some((existingCode) => {
            return existingCode.toLowerCase() === discountCode.toLowerCase()
          })

          if (!alreadyApplied) {
            codes.push(discountCode)
          }

          await dispatch('actionCartUpdate', {
            action: 'update',
            body: JSON.stringify({ discount: codes.join(',') }),
          })

          const appliedCodes = await dispatch('setDiscountCodes')

          const applied = appliedCodes.some((discount) => {
            return discount.code.toLowerCase() === discountCode.toLowerCase()
          })

          resolve(applied)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Clears the cart of all items.
     * - Cart attributes and cart note are not affected.
     * @returns {Promise}
     */
    clearCart({ commit, dispatch, state }) {
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', {
            items: state.response.items.map((item) => item.key),
            label: 'removing',
          })

          state.removedItems.forEach((item) => clearUndoRemoveTimer(item.key))
          commit('DELETE_REMOVED_ITEMS')

          const response = await dispatch('actionCartUpdate', {
            action: 'clear',
          })

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Clears cart attributes and note.
     * @returns {Promise}
     */
    clearCartAttributes({ commit, dispatch, state }) {
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', { label: 'updating' })

          const attributes = {}

          Object.keys(state.response.attributes).forEach((key) => {
            attributes[key] = null
          })

          const response = await dispatch('actionCartUpdate', {
            action: 'update',
            body: JSON.stringify({
              attributes,
              note: '',
            }),
          })

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Removes a line item and its add-ons from the cart, keeping a snapshot of
     * them so the customer can restore them.
     * - The item leaves the cart straight away so totals, discounts, and
     *   delivery thresholds stay accurate.
     * - The snapshot is discarded after `undoRemoveTimeout`.
     * @param {Object} lineItem - Grouped line item to remove.
     * @returns {Promise}
     */
    removeItemWithUndo({ commit, dispatch, getters }, lineItem) {
      return new Promise(async(resolve, reject) => {
        if (!lineItem?.key) {
          reject(new Error('No line item provided to remove'))
          return
        }

        const { key } = lineItem
        const updates = {}

        getLineItemGroup(lineItem).forEach((item) => {
          updates[item.key] = 0
        })

        const index = getters.getGroupedCart.items.findIndex((item) => {
          return item.key === key
        })

        try {
          const response = await dispatch('updateItemQuantities', { updates })

          commit('SET_REMOVED_ITEM', {
            index: Math.max(index, 0),
            key,
            lineItem,
            restoring: false,
          })

          clearUndoRemoveTimer(key)

          undoRemoveTimers[key] = setTimeout(() => {
            delete undoRemoveTimers[key]
            commit('DELETE_REMOVED_ITEM', key)
          }, undoRemoveTimeout)

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Removes a discount code from the cart.
     * - Shopify has no endpoint to remove a single code so the remaining codes
     *   are re-applied, an empty string removes them all.
     * @param {String} code - Discount code to remove.
     * @returns {Promise}
     */
    removeDiscountCode({ commit, dispatch, state }, code) {
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', { label: 'updating' })

          const discountCode = String(code || '').trim().toLowerCase()

          const codes = state.discountCodes
            .map((discount) => discount.code)
            .filter((existingCode) => existingCode.toLowerCase() !== discountCode)

          await dispatch('actionCartUpdate', {
            action: 'update',
            body: JSON.stringify({ discount: codes.join(',') }),
          })

          const appliedCodes = await dispatch('setDiscountCodes')

          resolve(appliedCodes)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Sets the discount codes currently applied to the cart.
     * - Uses the custom cart discount template as `cart.js` doesn't return
     *   discount codes.
     * - Don't query in storybook as can't access the cart template.
     * @returns {Promise}
     */
    setDiscountCodes({ commit }) {
      if (cnvs.storybook) {
        return Promise.resolve([])
      }

      return new Promise(async(resolve, reject) => {
        try {
          const url = `${cnvs.routes.cart.url}?view=discount&no-cache=${new Date().getTime()}`
          const response = await fetch(url)
          const discount = await response.json()
          const discountCodes = discount?.discount_codes || []

          commit('SET_DISCOUNT_CODES', discountCodes)
          resolve(discountCodes)

        } catch (error) {
          commit('SET_DISCOUNT_CODES', [])
          reject(error)
        }
      })
    },

    /**
     * Adds new event to history.
     * - Latest event is added to the start of the history array.
     * @param {Object} payload - Action payload.
     * @param {Object} payload.newCart - Updated cart.
     * @param {Object} payload.oldCart - Cart before update.
     */
    setHistory({ commit, state }, { newCart, oldCart }) {
      let action = false

      switch (state.status.label) {
        case 'adding':
          action = 'addToCart'
          break

        case 'removing':
        case 'clearing':
          action = 'removeFromCart'
          break
      }

      if (!action) {
        return
      }

      /**
       * Go through new cart and find new or changed items.
       * - Line item property changes are ignored.
       */
      const items = []

      newCart.items.forEach((newCartItem) => {
        const newItem = { ...newCartItem }

        const oldItem = oldCart.items.find((item) => {
          return item.key === newItem.key
        })

        /**
         * If new item doesn't exist in old cart then it's been added.
         */
        if (!oldItem) {
          items.push(newItem)
          return
        }

        if (oldItem.quantity === newItem.quantity) {
          return
        }

        /**
         * If new item's quantity doesn't match, find the difference and add to
         * items array.
         */
        const difference = newItem.quantity - oldItem.quantity
        newItem.quantity = difference

        items.push(newItem)
      })

      /**
       * Go through old cart and see if any don't exist in new cart.
       * - This means the item has been removed.
       */
      oldCart.items.forEach((oldItem) => {
        const newItem = newCart.items.find((item) => {
          return item.key === oldItem.key
        })

        if (newItem) {
          return
        }

        items.push(oldItem)
      })

      const event = {
        action,
        items,
        totalPriceChange: newCart.total_price - oldCart.total_price,
        totalQuantityChange: newCart.item_count - oldCart.item_count,
      }

      commit('SET_HISTORY', event)
    },

    /**
     * Set cart response after querying cart and custom cart data.
     * - Don't query in storybook as can't access `cart.js`.
     * @param {Boolean} [onHydration] - Setting response from initial site
     * hydration?
     * @returns {Promise}
     */
    setResponse({ commit, dispatch, state }, onHydration = false) {
      if (cnvs.storybook) {
        return null
      }

      /**
       * Query cart.js and extra data from custom cart template.
       */
      const queue = [
        fetch(`${cnvs.routes.cart.js}?no-cache=${new Date().getTime()}`),
        fetch(`${cnvs.routes.cart.url}?view=resource&no-cache=${new Date().getTime()}`),
      ]

      return new Promise(async(resolve, reject) => {
        try {
          const [cartResponse, extraResponse] = await Promise.all(queue)
          const cart = await cartResponse.json()
          const extra = await extraResponse.json()

          /**
           * Go through each line item and merge extra data.
           */
          const items = cart.items.map((item) => {
            const match = extra.find((extraItem) => {
              return extraItem.key === item.key
            })

            if (!match) {
              return item
            }

            return {
              ...item,
              ...match,
            }
          })

          /**
           * Merge cart items and set state.
           */
          const newCart = {
            ...cart,
            items,
          }

          const oldCart = { ...state.response }

          commit('SET_RESPONSE', newCart)

          /**
           * Update history.
           */
          if (!onHydration) {
            dispatch('setHistory', { newCart, oldCart })
          }

          /**
           * Update status.
           */
          commit('SET_STATUS', {
            label: state.status.label === 'loading' ? 'loaded' : 'updated',
          })

          resolve()

        } catch (error) {
          reject(error)
          cnvs.ReportError('Failed to set cart response', error)
        }
      })
    },

    /**
     * Set cart response manually from story.
     * - Should only be used by cart-based stories.
     * @param {Object} response - Manually formatted cart response.
     */
    setStoryResponse({ commit }, response) {
      commit('SET_RESPONSE', response)
    },

    /**
     * Set status of cart manually from story.
     * - Should only be used by cart-based stories.
     * @param {Object} payload - Action payload.
     * @param {Array} payload.items - Array of item keys being updated.
     * @param {String} payload.label - Status description, can be `loading`,
     * `loaded`, `adding`, `removing`, `clearing`, `updating`, `updated`, or
     * `error`.
     */
    setStoryStatus({ commit }, payload) {
      commit('SET_STATUS', payload)
    },

    /**
     * Restores a previously removed line item and its add-ons.
     * - Resolves without doing anything if the snapshot has already expired.
     * @param {String} key - Key of the removed line item.
     * @returns {Promise}
     */
    undoRemoveItem({ commit, dispatch, state }, key) {
      return new Promise(async(resolve, reject) => {
        const removedItem = state.removedItems.find((item) => item.key === key)

        if (!removedItem) {
          resolve()
          return
        }

        clearUndoRemoveTimer(key)
        commit('SET_REMOVED_ITEM', { ...removedItem, restoring: true })

        const items = getLineItemGroup(removedItem.lineItem).map((item) => {
          const payload = {
            id: item.variant_id || item.id,
            properties: item.properties || {},
            quantity: item.quantity,
          }

          const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id

          if (sellingPlanId) {
            payload.selling_plan = sellingPlanId
          }

          return payload
        })

        try {
          // Bypass `addItem` to avoid placeholders and conversion logging.
          commit('SET_STATUS', { items: [key], label: 'updating' })

          const response = await dispatch('actionCartUpdate', {
            action: 'add',
            body: JSON.stringify({ items }),
          })

          commit('DELETE_REMOVED_ITEM', key)
          resolve(response)

        } catch (error) {
          commit('SET_REMOVED_ITEM', { ...removedItem, restoring: false })

          undoRemoveTimers[key] = setTimeout(() => {
            delete undoRemoveTimers[key]
            commit('DELETE_REMOVED_ITEM', key)
          }, undoRemoveTimeout)

          reject(error)
        }
      })
    },

    /**
     * Updates cart attributes and note.
     * @param {Object} payload - Action payload.
     * @param {Object} [payload.attributes] - Attributes to set.
     * @param {String} [payload.note] - Cart note to set.
     * @returns {Promise}
     */
    updateCartAttributes({ commit, dispatch }, payload) {
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', { label: 'updating' })

          const response = await dispatch('actionCartUpdate', {
            action: 'update',
            body: JSON.stringify(payload),
          })

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Updates item(s) quantities in the cart.
     * - Uses /cart/update.js request to only affect items currently in cart.
     * - Payload should be sent in the format:
     *   `{
     *     '123:456': 2
     *   }`
     * - See https://shopify.dev/api/ajax/reference/cart#post-locale-cart-update-js
     * @param {Object} payload - Action payloads or item key.
     * @param {Object} payload.updates - Object of keys: quantities to update.
     * @returns {Promise}
     */
    updateItemQuantities({ commit, dispatch, state }, payload) {
      if (!payload.updates) {
        return false
      }

      const status = []
      const items = []
      let label = ''

      // Loop through all keys
      Object.keys(payload.updates).forEach((id) => {
        const field = typeof id === 'string' ? 'key' : 'id'
        const quantity = payload.updates[id]

        const lineItem = state.response.items.find((item) => {
          return item[field] === id
        })

        /**
         * Add status to array based on quantity.
         */
        if (typeof quantity === 'number') {
          if (quantity > lineItem.quantity) {
            status.push('adding')
          } else if (quantity < lineItem.quantity) {
            status.push('removing')
          }
        }

        items.push(id)
      })

      /**
       * Go through all statuses to see if they're the same.
       * - Set label if they're all the same.
       * - Set label to `updating` if mixed status.
       */
      const uniqueStatus = []

      status.forEach((statusItem) => {
        if (uniqueStatus.includes(statusItem)) {
          return
        }

        uniqueStatus.push(statusItem)
      })

      label = uniqueStatus.length > 1 ? 'updating' : uniqueStatus[0]

      /**
       * Action cart update.
       */
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', { items, label })

          const response = []

          const requestResponse = await dispatch('actionCartUpdate', {
            action: 'update',
            body: JSON.stringify(payload),
          })

          response.push(requestResponse)

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Updates item(s) in the cart.
     * - Uses /cart/change.js request to only affect items currently in cart.
     * - Item key can be provided directly as a string to remove it.
     * - Or an array of objects each containing id and either properties or
     *   quantity to update the item.
     * @param {Array|String} payload - Action payloads or item key.
     * @param {String} payload[].id - Item key to update.
     * @param {Object} [payload[].properties] - Item properties to update.
     * @param {Number} [payload[].quantity] - Quantity to change item to.
     * @returns {Promise}
     */
    updateItem({ commit, dispatch, state }, payload) {
      let body = ''
      let items = []
      let label = ''

      /**
       * Handle string (removes item).
       */
      if (typeof payload === 'string') {
        body = [{ id: payload, quantity: 0 }]
        items = [payload]
        label = 'removing'

      /**
       * Handle array.
       */
      } else {
        const status = []

        body = payload.map((object) => {
          const { id, properties } = object
          let { quantity } = object
          const field = typeof id === 'string' ? 'key' : 'id'

          /**
           * Find line item being updated.
           * - To find the key of the line item no matter what is passed.
           */
          const lineItem = state.response.items.find((item) => {
            return item[field] === id
          })

          /**
           * Add status to array based on quantity.
           */
          if (typeof quantity === 'number') {
            if (quantity > lineItem.quantity) {
              status.push('adding')
            } else if (quantity < lineItem.quantity) {
              status.push('removing')
            }
          }

          items.push(lineItem.key)

          /**
           * If no quantity then set to current line item quantity.
           * - If other line item has matching properties and id then sum
           *   quantity.
           * - Adds compatibility for when id is id, rather than key.
           */
          if (typeof quantity !== 'number') {
            status.push('updating')

            const matchingItem = state.response.items.find((item) => {
              return (
                item.id === lineItem.id &&
                item.properties === properties
              )
            })

            if (matchingItem) {
              quantity = lineItem.quantity + matchingItem.quantity
            } else {
              quantity = lineItem.quantity
            }
          }

          return { id, properties, quantity }
        })

        /**
         * Go through all statuses to see if they're the same.
         * - Set label if they're all the same.
         * - Set label to `updating` if mixed status.
         */
        const uniqueStatus = []

        status.forEach((statusState) => {
          if (uniqueStatus.includes(statusState)) {
            return
          }

          uniqueStatus.push(statusState)
        })

        label = uniqueStatus.length > 1 ? 'updating' : uniqueStatus[0]
      }

      /**
       * Action cart update.
       */
      return new Promise(async(resolve, reject) => {
        try {
          commit('SET_STATUS', { items, label })

          const response = []
          const queueLength = [...body].length

          for (let index = 0; index < queueLength; index++) {
            if (index) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((timeoutResolve) => {
                setTimeout(() => {
                  timeoutResolve()
                }, 1000)
              })
            }

            const item = body.shift()

            // eslint-disable-next-line no-await-in-loop
            const itemResponse = await dispatch('actionCartUpdate', {
              action: 'change',
              body: JSON.stringify(item),
              itemsLeft: body.length,
            })

            response.push(itemResponse)
          }

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },
  },
}
